import {Component, Input, OnInit, ViewChildren, inject} from '@angular/core';
import {I18nService} from '../../../../_services/gui/i18n.service';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule} from '@angular/forms';
import {i18nKeyMap} from '../../../../_utils/lang';
import {Observable} from 'rxjs';
import {UserTimeRestriction} from '../../../../_data/admin/user';
import {DayAccessTimeComponent} from './day-access-time/day-access-time.component';
import {shortDaysOfWeek} from '../../../../_constants/constants';
import {ValidationErrorsComponent} from '../../../../common/forms/validation-errors/validation-errors.component';
import {KeyValuePipe} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';

/**
 * Manages the "Access Times" section of the Create User form.  When the Create User component handled everything, and after fixing Chrome's tab issue, it was spaghetti.
 */
@Component({
    selector: 'app-access-times',
    templateUrl: './access-times.component.html',
    styleUrls: ['./access-times.component.scss'],
    imports: [ReactiveFormsModule, ValidationErrorsComponent, DayAccessTimeComponent, KeyValuePipe, TranslateModule]
})
export class AccessTimesComponent implements OnInit {

  /** The overall create-user FormGroup.  Will be updated by this component and child components */
  @Input() createUserFormGroup: UntypedFormGroup;

  /** An observable that will emit when the parent create-user form has attempted a submission.  Used to know when to show validation errors.  */
  @Input() userFormSubmitted$: Observable<boolean>;

  @ViewChildren(DayAccessTimeComponent) dayAccessTimeComponents: DayAccessTimeComponent[];

  shortDaysOfWeek = shortDaysOfWeek;

  userFormSubmitted: boolean;
  daysOfWeek: Map<string, string>;
  timeZones: Map<string, string>;

  private readonly i18nService = inject(I18nService);
  private readonly formBuilder = inject(UntypedFormBuilder);

  constructor() {
    const i18nService = this.i18nService;

    // Get the days of the week for the loaded language
    i18nService.getTranslationMap(
      i18nKeyMap('content.keyed.days-of-week.', ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'])
    ).subscribe(value => this.daysOfWeek = value);
    // Get the timezones for the loaded language
    i18nService.timeZones.subscribe(value => this.timeZones = value);
  }

  /** init lifecycle */
  ngOnInit(): void {
    /*
     * Create the sub form group that will hold yet more groups for each day.
     * allowedTimes: {
     *    sun: {allowed: '', startTime: '', endTime: ''},
     *    mon: {allowed: '', startTime: '', endTime: ''}
     * }
     * Each day's component will add its own sub group.
     */
    this.createUserFormGroup.setControl('allowedTimes', this.formBuilder.group({}));
    this.createUserFormGroup.setControl('timeZone', new UntypedFormControl('', Validators.required));

    //Tie the userFormSubmitted$ field to the Subject state
    this.userFormSubmitted$.subscribe(v => this.userFormSubmitted = v);
  }

  /**
   * When the user clicks the "apply to all" button, that DayAccessTime component will emit the data to this method.
   * This method then needs to tell all of the other DayAccessTime components that they need to copy that data.
   *
   * @see {@link DayAccessTimeComponent#update}
   */
  applyTimeToAll(userTimeRestriction: UserTimeRestriction) {
    console.debug('Applying time to all other days', userTimeRestriction);
    console.debug(this.dayAccessTimeComponents);
    this.dayAccessTimeComponents.forEach(comp => comp.update(userTimeRestriction));
  }

  /**
   * Updates the child DayAccessTime components with the supplied time restrictions
   * Used, for example, by the create-user form when loading an existing user in order to show their set times.
   *
   * @see {@link DayAccessTimeComponent#updateFrom}
   */
  applyTimes(timeZone: string | number, userTimeRestrictions: UserTimeRestriction[]) {
    timeZone = timeZone.toString();
    console.debug(`Loading timezone ${timeZone} - ${typeof timeZone}`);
    console.debug('Loaded timeZones', this.timeZones);
    if(this.timeZones.has(timeZone)) {
      console.debug(`User's timezone: ${timeZone} - ${this.timeZones.get(timeZone)}`);
      this.createUserFormGroup.controls.timeZone.setValue(timeZone);
    } else {
      console.warn(`User's timezone is invalid (${timeZone})`);
    }

    console.debug('Loading time restrictions', userTimeRestrictions);
    this.dayAccessTimeComponents.forEach(comp => comp.updateFrom(userTimeRestrictions));
  }

  get daysOfWeekKeys() {
    return this.daysOfWeek.keys();
  }

  get allowedTimesFormGroup(): UntypedFormGroup {
    return this.createUserFormGroup.controls.allowedTimes as UntypedFormGroup;
  }
}
